import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { ContextMenu, CrudPageLayout } from '@core/components';
import { useConfirmDialog } from '@core/hooks/useConfirmDialog';
import { useListFilter } from '@core/hooks/useListFilter';
import { useMenu } from '@core/hooks/useMenu';
import {
  type Design,
  type DesignPage,
  deleteDesign,
  designKeys,
} from '../../api/design';
import { DesignDialogSwitch } from '../components/DesignDialogSwitch';
import DesignFilterDrawer from '../components/DesignFilterDrawer';
import DesignGrid from '../components/DesignGrid';
import { useDesignPages } from '../hooks/useDesignPages';
import { navItems } from '../navigation';
import {
  EMPTY_DESIGN_FILTERS,
  countActiveDesignFilters,
  designFiltersToQueryParams,
} from '../utils/design';

/** About the first two rows at most breakpoints. */
const PRIORITY_CARD_COUNT = 12;

const DESIGN_COLUMNS = { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 };

const DESIGN_FILTER_CONFIG = {
  empty: EMPTY_DESIGN_FILTERS,
  countActive: countActiveDesignFilters,
};

const Designs = () => {
  const queryClient = useQueryClient();

  const {
    anchorEl: menuAnchor,
    item: menuDesign,
    isOpen: isMenuOpen,
    openMenu: openDesignMenu,
    closeMenu,
  } = useMenu<Design>();

  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDesign, setEditingDesign] = useState<Design | null>(null);

  const {
    filters,
    setFilters,
    clearFilters,
    filterOpen,
    openFilters,
    closeFilters,
    activeFilterCount,
    getQuery,
  } = useListFilter({
    filter: DESIGN_FILTER_CONFIG,
    searchTerm,
    toQueryParams: designFiltersToQueryParams,
  });
  const query = getQuery();

  const pages = useDesignPages(query);
  const { isError } = pages;

  const updateCachedPages = useCallback(
    (update: (page: DesignPage) => DesignPage) => {
      queryClient.setQueriesData<InfiniteData<DesignPage>>(
        { queryKey: designKeys.pages },
        (cached) =>
          cached ? { ...cached, pages: cached.pages.map(update) } : cached
      );
    },
    [queryClient]
  );

  const invalidatePickers = useCallback(
    () => queryClient.invalidateQueries({ queryKey: designKeys.pickers }),
    [queryClient]
  );

  const handleSaved = useCallback(
    async (saved: Design, mode: 'create' | 'update') => {
      if (mode === 'create') {
        await queryClient.resetQueries({ queryKey: designKeys.all });
        return;
      }
      updateCachedPages((page) => ({
        ...page,
        items: page.items.map((design) =>
          design.id === saved.id ? saved : design
        ),
      }));
      await invalidatePickers();
    },
    [queryClient, updateCachedPages, invalidatePickers]
  );

  const deleteAndRemoveDesign = useCallback(
    async (deleted: Design) => {
      await deleteDesign(deleted.id);
      updateCachedPages((page) => ({
        ...page,
        items: page.items.filter((design) => design.id !== deleted.id),
        total: Math.max(0, page.total - 1),
      }));
    },
    [updateCachedPages]
  );

  const openCreate = useCallback(() => {
    setEditingDesign(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((design: Design) => {
    setEditingDesign(design);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setEditingDesign(null);
    setDialogOpen(false);
  }, []);

  const {
    item: deleteConfirm,
    request: requestDelete,
    cancel: cancelDelete,
    confirm: confirmDelete,
    loading: deleteLoading,
  } = useConfirmDialog<Design>({
    onConfirm: deleteAndRemoveDesign,
    onSuccess: invalidatePickers,
    successMessage: 'Design deleted',
    errorMessage: 'Failed to delete design',
  });

  const openMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, design: Design) => {
      event.stopPropagation();
      openDesignMenu(event, design);
    },
    [openDesignMenu]
  );

  const emptyMessage = isError
    ? 'Failed to load designs.'
    : activeFilterCount === 0 && !searchTerm.trim()
      ? 'No designs yet. Add one to get started.'
      : 'No designs match your search or filters.';

  return (
    <CrudPageLayout
      navItems={navItems}
      path="/designs"
      searchConfig={{
        placeholder: 'Search designs...',
        onSearch: setSearchTerm,
      }}
      filterConfig={{
        activeCount: activeFilterCount,
        onOpen: openFilters,
      }}
      addConfig={{ label: 'Add Design', onAdd: openCreate }}
      addEditDialog={
        dialogOpen ? (
          <DesignDialogSwitch
            onClose={closeDialog}
            design={editingDesign}
            onSuccess={handleSaved}
          />
        ) : null
      }
      deleteConfig={{
        title: 'Delete design',
        item: deleteConfirm,
        getContent: (design) =>
          `Are you sure you want to delete "${design.code}"?`,
        onConfirm: confirmDelete,
        onCancel: cancelDelete,
        loading: deleteLoading,
      }}
    >
      <DesignGrid
        pages={pages}
        columns={DESIGN_COLUMNS}
        priorityCount={PRIORITY_CARD_COUNT}
        emptyMessage={emptyMessage}
        minHeight={250}
        onOpen={openEdit}
        onMenu={openMenu}
      />

      <DesignFilterDrawer
        open={filterOpen}
        filters={filters}
        onApply={setFilters}
        onClear={clearFilters}
        onClose={closeFilters}
      />

      {menuDesign != null ? (
        <ContextMenu
          open={isMenuOpen}
          onClose={closeMenu}
          anchorEl={menuAnchor}
          items={[
            { label: 'Edit', onClick: () => openEdit(menuDesign) },
            {
              label: 'Delete',
              destructive: true,
              onClick: () => requestDelete(menuDesign),
            },
          ]}
        />
      ) : null}
    </CrudPageLayout>
  );
};

export default Designs;
